import React, { FunctionComponent } from 'react'
import { useFormState } from 'react-final-form'

import rjsfFields from '@rjsf/core/lib/components/fields'
import { includes } from 'lodash-es'
import styled from 'styled-components/native'

import {
  audInformedConsent,
  cgtInformedConsent,
  cleInformedConsent,
  coachingInformedConsent,
  coachingInformedConsentNonUS,
  daInformedConsent,
  dbtInformedConsent,
  informedConsent,
  medsInformedConsent,
  nscInformedConsent,
  singleSessionCoachingInformedConsent,
  teensTherapyInformedConsent,
} from '@lyrahealth-inc/shared-app-logic/src/features/assignments/constants'
import { isFieldDisplayed } from '@lyrahealth-inc/shared-app-logic/src/features/assignments/utils'

import { type UICoreFieldProps } from './types'
import { OrDivider } from '../../atoms/orDivider/OrDivider'
import { ClinicalScreenerHeader } from '../../molecules/clinicalScreenerHeader/ClinicalScreenerHeader'
import { DAProviderSessionRate } from '../../molecules/daProviderSessionRate/DAProviderSessionRate'
import { ExperienceWithCareHeader } from '../../molecules/experienceWithCareHeader/ExperienceWithCareHeader'
import { HealthPlanCardUploadHeaderTitle } from '../../molecules/healthPlanCardUploadHeaderTitle/HealthPlanCardUploadHeaderTitle'
import { InformedConsent } from '../../molecules/informedConsent/InformedConsent'
import { SUDSTable, TeensDistressTable } from '../../molecules/sudsTable/SUDSTable'
import { AnimatedWrapper } from '../../templates/animatedWrapper/AnimatedWrapper'
import { Flex1View } from '../../templates/content/CommonViews'
import { tID } from '../../utils/utils'

const { SchemaField } = rjsfFields

const ConditionContainer = styled(Flex1View)<{ hide: boolean }>`
  ${({ hide }) =>
    hide &&
    `
    display: none;
    opacity: 0;
  `}
`

const SchemaFieldContainer = styled(SchemaField)`
  flex-grow: 1;
`

/**
 * Add custom components here such as SUDSTable, InformedConsent and FeedbackStars
 */

const addTitleStar = (props: UICoreFieldProps): UICoreFieldProps => {
  const title = props.schema?.title
  if (props.registry?.formContext.showAsteriskForRequiredFields && title !== undefined) {
    if (Boolean(props.schema?.validation?.isRequired) && title[title.length - 1] !== '*') {
      const newProps = { ...props, schema: { ...props.schema, title: `${props.schema.title} *` } }
      return newProps
    }
  }
  return props
}

const CustomSchemaField: React.FC<UICoreFieldProps> = (props) => {
  const newProps = addTitleStar(props)
  if (newProps.schema?.show === false) {
    return null
  }
  if (newProps.schema?.condition) {
    return <Condition {...newProps} />
  }
  return <SchemaFieldContainer {...newProps} />
}

const Condition: React.FC<UICoreFieldProps> = (props) => {
  const { values } = useFormState()
  const limitedFields = props.registry?.formContext?.limitedFields
  const hide = limitedFields
    ? !includes(limitedFields, props.schema?.name)
    : !isFieldDisplayed({
        condition: props.schema?.condition,
        formValues: { ...values, ...props.registry?.formContext?.externalValues },
        formFields: props.registry?.formContext?.schema?.properties,
        name: props.schema?.name,
      })
  const newProps = props?.schema ? { ...props, schema: { ...props.schema, hidden: hide } } : props
  if (hide && newProps.schema?.condition?.noRender) {
    return null
  } else {
    return (
      <ConditionContainer
        hide={hide}
        accessibilityHidden={hide}
        testID={tID(`conditionField-${hide ? 'hide' : 'show'}`)}
      >
        <SchemaField {...newProps} />
      </ConditionContainer>
    )
  }
}

export const fields: { [name: string]: FunctionComponent } = {
  UnsupportedField: ({ schema: name }: UICoreFieldProps) => {
    console.error(`Unsupported field defined - ${name.name}`)
    return null
  },
  SchemaField: CustomSchemaField,
  SUDSTable: () => <SUDSTable />,
  TeensDistressTable: () => <TeensDistressTable />,
  OrDivider: ({ schema: { content } }: UICoreFieldProps) => <OrDivider text={content} marginVertical={0} />,
  InformedConsent: ({ schema: { title, name } }: UICoreFieldProps) => (
    <InformedConsent title={title} name={name} content={informedConsent} />
  ),
  MedsInformedConsent: ({ schema: { title, name } }: UICoreFieldProps) => (
    <InformedConsent title={title} name={name} content={medsInformedConsent} />
  ),
  CLEInformedConsent: ({ schema: { title, name } }: UICoreFieldProps) => (
    <InformedConsent title={title} name={name} content={cleInformedConsent} />
  ),
  CoachingInformedConsent: ({ schema: { title, name } }: UICoreFieldProps) => (
    <InformedConsent title={title} name={name} content={coachingInformedConsent} />
  ),
  CoachingInformedConsentNonUS: ({ schema: { title, name } }: UICoreFieldProps) => (
    <InformedConsent title={title} name={name} content={coachingInformedConsentNonUS} />
  ),
  SingleSessionCoachingInformedConsent: ({ schema: { title, name } }: UICoreFieldProps) => (
    <InformedConsent title={title} name={name} content={singleSessionCoachingInformedConsent} />
  ),
  AudInformedConsent: ({ schema: { title, name } }: UICoreFieldProps) => (
    <InformedConsent title={title} name={name} content={audInformedConsent} />
  ),
  TeensTherapyInformedConsent: ({ schema: { title, name } }: UICoreFieldProps) => (
    <InformedConsent title={title} name={name} content={teensTherapyInformedConsent} />
  ),
  DirectAccessInformedConsent: ({ schema: { title, name } }: UICoreFieldProps) => (
    <InformedConsent title={title} name={name} content={daInformedConsent} />
  ),
  DbtInformedConsent: ({ schema: { title, name } }: UICoreFieldProps) => (
    <InformedConsent title={title} name={name} content={dbtInformedConsent} />
  ),
  NscInformedConsent: ({ schema: { title, name } }: UICoreFieldProps) => (
    <InformedConsent title={title} name={name} content={nscInformedConsent} />
  ),
  CgtInformedConsent: ({ schema: { title, name } }: UICoreFieldProps) => (
    <InformedConsent title={title} name={name} content={cgtInformedConsent} />
  ),
  ClinicalScreenerHeader: ({
    schema: { title, subTitle, name },
    formContext: { animatedSharedValues },
  }: UICoreFieldProps) => (
    <AnimatedWrapper animatedSharedValues={animatedSharedValues}>
      <ClinicalScreenerHeader title={title} subTitle={subTitle} name={name} />
    </AnimatedWrapper>
  ),
  ExperienceWithCareHeader: ({ schema: { title, name }, formContext: { animatedSharedValues } }: UICoreFieldProps) => (
    <AnimatedWrapper animatedSharedValues={animatedSharedValues}>
      <ExperienceWithCareHeader title={title} name={name} />
    </AnimatedWrapper>
  ),
  HealthPlanCardUploadHeader: ({ uiSchema }: UICoreFieldProps) => {
    const hideUploadOptionalBadge = uiSchema?.['ui:options']?.hideUploadOptionalBadge as boolean | undefined
    return <HealthPlanCardUploadHeaderTitle hideOptionalBadge={hideUploadOptionalBadge} />
  },
  DASessionRate: ({ schema: { sessionRate, isIneligible } }: UICoreFieldProps) => (
    <DAProviderSessionRate sessionRate={sessionRate} isIneligible={isIneligible} />
  ),
}
